import {
    BaseFeatureArgs,
    FixedFilterGenerator,
    InitialQueryState,
    SkipOrArgs,
} from '@common/types/queryState';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import { useProjectedFeatureQuery } from '@common/hooks/query';
import { composeGenerators } from '@common/utils/query/composeGenerators';
import { useSelectedProductionId } from '@features/productions';
import { useGetClipsQuery } from '../api';
import { CLIP_KEYS } from '../constants';
import { Clip, GetClipsArgs } from '../types';

export interface UseClipCollectionResolverArgs extends BaseFeatureArgs {
    assetId: string;
}

const clipByIdGenerator: FixedFilterGenerator<GetClipsArgs> = (args) => {
    return [`id:Annotation_${args.assetId}`];
};

export const useClipCollectionResolver = (
    initialArgs: InitialQueryState = {},
    args: SkipOrArgs<UseClipCollectionResolverArgs>,
) => {
    const productionId = useSelectedProductionId();

    const queryArgs: SkipOrArgs<GetClipsArgs> = useMemo(() => {
        if (args === skipToken || !args.assetId || !productionId)
            return skipToken;

        const mergedGenerators = composeGenerators(
            clipByIdGenerator,
            args.fixedFilterGenerators,
        );

        return {
            ...args,
            productionId,
            fixedFilterGenerators: mergedGenerators,
            fields: ['collectionId'],
            keyMap: CLIP_KEYS,
        } as GetClipsArgs;
    }, [args, productionId]);

    const { data, ...queryResult } = useProjectedFeatureQuery<
        Clip,
        GetClipsArgs,
        'collectionId'
    >(
        { ...initialArgs, pageIndex: 0, pageSize: 1 },
        useGetClipsQuery,
        queryArgs,
        ['collectionId'],
        { paramsOverride: { pageIndex: 0, pageSize: 1 }, skipUrlSync: true },
    );

    const collectionId = data?.[0]?.collectionId;

    return {
        collectionId,
        ...queryResult,
    };
};
